export interface StorageManagerAdapter {
  estimate: () => Promise<{ quota?: number; usage?: number }>
  persist: () => Promise<boolean>
  persisted: () => Promise<boolean>
}

export type StorageLevel = 'normal' | 'warning' | 'critical' | 'unknown'

export interface StorageStatus {
  supported: boolean
  persisted: boolean
  persistenceRequested: boolean
  usage: number
  quota: number
  percentage: number
  warning: boolean
  critical: boolean
  level: StorageLevel
}

export function calculateStorageStatus(
  usage: number | undefined,
  quota: number | undefined,
  persisted: boolean,
  persistenceRequested: boolean,
  supported = true,
): StorageStatus {
  const safeUsage = Number.isFinite(usage) && (usage ?? 0) >= 0 ? (usage ?? 0) : 0
  const safeQuota = Number.isFinite(quota) && (quota ?? 0) > 0 ? (quota ?? 0) : 0
  const percentage = safeQuota > 0 ? Math.min((safeUsage / safeQuota) * 100, 100) : 0
  const critical = safeQuota > 0 && percentage >= 85
  const warning = safeQuota > 0 && percentage >= 70
  return {
    supported,
    persisted,
    persistenceRequested,
    usage: safeUsage,
    quota: safeQuota,
    percentage,
    warning,
    critical,
    level:
      !supported || safeQuota === 0
        ? 'unknown'
        : critical
          ? 'critical'
          : warning
            ? 'warning'
            : 'normal',
  }
}

function getBrowserStorage(): StorageManagerAdapter | undefined {
  if (typeof navigator === 'undefined' || !navigator.storage) return undefined
  const storage = navigator.storage
  if (!storage.estimate || !storage.persist || !storage.persisted) return undefined
  return {
    estimate: () => storage.estimate(),
    persist: () => storage.persist(),
    persisted: () => storage.persisted(),
  }
}

export async function inspectStorage(
  adapter: StorageManagerAdapter | undefined = getBrowserStorage(),
  requestPersistence = true,
): Promise<StorageStatus> {
  if (!adapter) return calculateStorageStatus(0, 0, false, false, false)
  const wasPersisted = await adapter.persisted()
  const persistenceRequested = requestPersistence && !wasPersisted
  const persisted = persistenceRequested ? await adapter.persist() : wasPersisted
  const estimate = await adapter.estimate()
  return calculateStorageStatus(estimate.usage, estimate.quota, persisted, persistenceRequested)
}
